// 分享工具：编码/解码载荷、生成一次性 Token、构建分享链接
import { supabase } from '../supabaseClient';
export const generateVersionStamp = () => {
  const d = new Date();
  const ts = `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}-${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}${String(d.getSeconds()).padStart(2,'0')}`;
  return `v${ts}`;
};

// 生成一次性访问 Token（Base62）
export const generateRandomToken = (length = 24) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < length; i++) {
    const idx = Math.floor(Math.random() * chars.length);
    token += chars[idx];
  }
  return token;
};

export const encodePayloadBase64 = (obj) => {
  try {
    const json = JSON.stringify(obj);
    // 兼容中文的 base64 编码
    return btoa(unescape(encodeURIComponent(json)));
  } catch (_) {
    return '';
  }
};

export const decodePayloadBase64 = (b64) => {
  try {
    const json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
};

export const buildShareUrl = ({ payload, token }) => {
  const base64 = encodePayloadBase64(payload);
  const origin = window.location.origin;
  const path = window.location.pathname; // 适配 hash 路由
  return `${origin}${path}#/share?share=${encodeURIComponent(base64)}&token=${encodeURIComponent(token)}`;
};

// ======= 签名与防篡改 =======
// 计算 SHA-256 十六进制摘要（优先使用 WebCrypto）
export const sha256Hex = async (text) => {
  try {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      const enc = new TextEncoder().encode(text);
      const digest = await window.crypto.subtle.digest('SHA-256', enc);
      const bytes = Array.from(new Uint8Array(digest));
      return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) {}
  try {
    // Node 环境回退（用于测试）
    const cryptoMod = await import('crypto');
    return cryptoMod.createHash('sha256').update(text).digest('hex');
  } catch (_) {
    // 最后回退：非加密哈希，避免崩溃（仅用于不可用环境）
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    return (h >>> 0).toString(16);
  }
};

export const computeShareSignature = async (b64, token) => {
  return sha256Hex(`${b64}|${token}`);
};

// 构建带签名的分享 URL（异步）
export const buildShareUrlSigned = async ({ payload, token }) => {
  const base64 = encodePayloadBase64(payload);
  const origin = window.location.origin;
  const path = window.location.pathname;
  const sig = await computeShareSignature(base64, token);
  return `${origin}${path}#/share?share=${encodeURIComponent(base64)}&token=${encodeURIComponent(token)}&sig=${sig}`;
};

// 校验分享签名
export const verifyShareSignature = async ({ b64, token, sig }) => {
  try {
    const expected = await computeShareSignature(b64, token);
    return String(expected) === String(sig);
  } catch (_) {
    return false;
  }
};

const USED_KEY = 'share_tokens_used';
export const isTokenUsed = (token) => {
  try {
    const raw = localStorage.getItem(USED_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw);
    return Boolean(map[token]);
  } catch (_) {
    return false;
  }
};

export const markTokenUsed = (token) => {
  try {
    const raw = localStorage.getItem(USED_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[token] = true;
    localStorage.setItem(USED_KEY, JSON.stringify(map));
  } catch (_) {
    // noop
  }
};

// ======= 短链接支持（Supabase，可选，失败时回退为长链接）=======
export const generateSlug = (length = 8) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  let s = '';
  for (let i = 0; i < length; i++) {
    const idx = Math.floor(Math.random() * chars.length);
    s += chars[idx];
  }
  return s;
};

export const createShortLink = async ({ b64, token, sig, expiresAt, policy, usesLeft = 1 }) => {
  try {
    const slug = generateSlug(8);
    const { data, error } = await supabase
      .from('short_links')
      .insert({ slug, payload_b64: b64, token, sig, expires_at: expiresAt, uses_left: usesLeft, policy })
      .select('slug')
      .single();
    if (error) throw error;
    const origin = window.location.origin;
    const path = window.location.pathname;
    return `${origin}${path}#/share?s=${encodeURIComponent(data.slug)}`;
  } catch (e) {
    return null;
  }
};

export const fetchShortLinkRow = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('short_links')
      .select('*')
      .eq('slug', slug)
      .single();
    if (error) return null;
    return data;
  } catch (_) {
    return null;
  }
};

export const decrementShortLinkUsesLeft = async (slug) => {
  try {
    const row = await fetchShortLinkRow(slug);
    if (!row) return false;
    const left = Number(row.uses_left || 0);
    if (left <= 0) return false;
    const { error } = await supabase
      .from('short_links')
      .update({ uses_left: left - 1, last_access_at: new Date().toISOString() })
      .eq('slug', slug);
    if (error) return false;
    return true;
  } catch (_) {
    return false;
  }
};

export const buildShareUrlPreferShort = async ({ payload, token, expiresAt, policy, preferShort = true }) => {
  const b64 = encodePayloadBase64(payload);
  const sig = await computeShareSignature(b64, token);
  if (preferShort) {
    const shortUrl = await createShortLink({ b64, token, sig, expiresAt, policy, usesLeft: (policy?.maxUses ?? 1) });
    if (shortUrl) return shortUrl;
  }
  return buildShareUrlSigned({ payload, token });
};